import { getCopyableCivicAddress } from '@rsos/utils/getCopyableCivicAddress';
import { parseADRtoCopy } from '@rsos/utils/parseADRToCopyUtils';
import { convertUnixTimestamp } from '@rsos/utils/timeHelpers';
import { isEmpty } from '@rsos/utils/validators';
import { getAlertHeaderText } from '../DataCards/utils';

/**
 * Taken from emergency-data formatValue, converts to 7 decimal places and removes trailing zeros.
 * If a whole number also removes the '.'
 * @param {string} value
 * @returns string
 */
const formatValue = value => {
  return parseFloat(value).toFixed(7).replace(/0+$/, '').replace(/[.]+$/, '');
};

export const getAlertLocationData = (alert, what3words) => {
  if (!alert) return null;

  const {
    civic = {},
    geodetic = {},
    created_at = 0,
  } = alert.location_history?.[0] || {};
  const serviceProviderName =
    alert.service_provider_name &&
    (alert.service_provider_name.display_name || alert.service_provider_name);

  const address = getCopyableCivicAddress(civic);

  const isAltitudeEmpty = isEmpty(geodetic.altitude);

  const formattedLatitude = formatValue(geodetic.latitude);
  const formattedLongitude = formatValue(geodetic.longitude);
  const formattedUncertaintyRadius = Number(
    geodetic.uncertainty_radius,
  ).toFixed(1);

  return {
    alertID: alert.alert_id,
    alertType: getAlertHeaderText(
      alert.emergency_type,
      alert.site_type?.display_name,
    ),
    source: serviceProviderName,
    locationTime: convertUnixTimestamp(parseFloat(created_at)),
    estimatedAddress: address,
    latitude: isNaN(formattedLatitude) ? null : formattedLatitude,
    longitude: isNaN(formattedLongitude) ? null : formattedLongitude,
    what3words: what3words ? `https://what3words.com/${what3words}` : null,
    uncertaintyRadius: isNaN(formattedUncertaintyRadius)
      ? null
      : `${formattedUncertaintyRadius} meters`,
    altitude: !isAltitudeEmpty
      ? `${geodetic.altitude.toFixed(1)} meters`
      : null,
    verticalUncertainty:
      !isAltitudeEmpty && !isEmpty(geodetic.vertical_uncertainty)
        ? `${geodetic.vertical_uncertainty.toFixed(1)} meters`
        : null,
    estimatedFloorLevel: geodetic.estimated_floor_level,
  };
};

/**
 * Parses through the ADR Array, removes hidden formats and no-types, and returns a copyable string
 * @param {Array} adrList - Full Array of ADRs found in adrData
 * @returns {String} ADR object converted to a string
 */
export const getCopyableADR = adrList => {
  if (!adrList) {
    return null;
  }

  let jsonString = '';
  adrList.forEach((dataPoint, index) => {
    if (dataPoint.display_schema) {
      const dataString = parseADRtoCopy(
        dataPoint.display_schema,
        dataPoint.body,
      );
      if (index > 0 && jsonString) {
        jsonString += '\n';
      }
      jsonString += dataString ? `Additional Data\n${dataString}` : '';
    }
  });

  const returnString = jsonString ? jsonString : null;
  return returnString;
};
